import { useForm } from "react-hook-form";
import { Button } from "./ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { z } from "zod";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiFetch, filterPaymentMethods } from "@/utils";
import { PaymentMethodSelect } from "./PaymentMethodSelect";
import { Loader2 } from "lucide-react";
import ConfirmModal from "./common/ConfirmationModal";

const FIAT_DECIMAL = 2;
const fiatRegExp = new RegExp(`^\\d+\\.\\d{${FIAT_DECIMAL + 1},}$`);

const buyTokensScheme = z.object({
  fiatAmount: z
    .string()
    .refine((val) => !fiatRegExp.test(val), {
      message:
        "Input value can contain only up to 2 digits after the decimal point",
    })
    .refine((val) => parseFloat(val) >= 20, {
      message: "Minimum amount is 20 USD",
    }),
  paymentMethod: z.enum(["debit-credit-card", "apple-pay", "google-pay"]),
});

export default function BuyTokensForm({
  onBuyTokens,
}: {
  onBuyTokens: (
    fiatAmount: string,
    cryptoAmount: string,
    paymentMethod: "debit-credit-card" | "apple-pay" | "google-pay"
  ) => Promise<void>;
}) {
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [isOrderProcessing, setIsOrderProcessing] = useState(false);
  const [isAmountsLoading, setIsAmountsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<z.infer<
    typeof buyTokensScheme
  > | null>(null);

  const [quotes, setQuotes] = useState({
    usdcAmount: "",
    sechAmount: "",
    gatewayFee: "",
  });

  const form = useForm<z.infer<typeof buyTokensScheme>>({
    resolver: zodResolver(buyTokensScheme),
    defaultValues: { fiatAmount: "20", paymentMethod: "debit-credit-card" },
    mode: "all",
    shouldUseNativeValidation: true,
  });

  const fiatAmount = form.watch("fiatAmount");

  const onFiatAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAmountsLoading(true);
    const fiatValue = e.target.value;

    if (debounceTimeout) clearTimeout(debounceTimeout);

    if (fiatRegExp.test(fiatValue)) {
      setIsAmountsLoading(false);
      return; // Prevent execution if more than 2 decimals
    }

    form.setValue("fiatAmount", fiatValue);

    if (!fiatValue) {
      setQuotes({ usdcAmount: "0", sechAmount: "0", gatewayFee: "0" });
    }

    if (parseFloat(fiatValue) < 20) {
      setQuotes({ usdcAmount: "0", sechAmount: "0", gatewayFee: "0" });
      setIsAmountsLoading(false);
      return;
    }

    setDebounceTimeout(
      setTimeout(async () => {
        try {
          const tokensPriceResponse = await apiFetch(
            `/v1/tokenPurchase/quote?fiatAmount=${fiatValue}`,
            { withCredentials: true }
          );
          const tokensPriceResult = await tokensPriceResponse.json();

          setQuotes({
            usdcAmount: tokensPriceResult?.data?.usdcAmount,
            sechAmount: tokensPriceResult?.data?.sechAmount
              ? Number(tokensPriceResult?.data?.sechAmount)?.toFixed(2)
              : "0",
            gatewayFee: tokensPriceResult?.data?.gatewayFee,
          });
        } catch (error) {
          console.error("Error fetching tokens amounts:", error);
        } finally {
          setIsAmountsLoading(false);
        }
      }, 400)
    );
  };

  const onSubmit = (values: z.infer<typeof buyTokensScheme>) => {
    setPendingValues(values);
    setShowConfirmModal(true);
  };

  const handleConfirmBuy = async () => {
    if (!pendingValues) return;

    if (!quotes.usdcAmount) {
      console.error("Quote not loaded yet");
      return;
    }

    try {
      const fiatAmountParsed = pendingValues.fiatAmount;
      if (!fiatAmountParsed || fiatRegExp.test(fiatAmountParsed)) return;

      setIsOrderProcessing(true);

      await onBuyTokens(
        fiatAmountParsed,
        quotes.usdcAmount,
        pendingValues.paymentMethod
      );

      form.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setIsOrderProcessing(false);
      setShowConfirmModal(false);
      setPendingValues(null);
    }
  };

  useEffect(() => {
    const getInitialQuote = async () => {
      try {
        const tokensPriceResponse = await apiFetch(
          `/v1/tokenPurchase/quote?fiatAmount=${fiatAmount}`,
          { withCredentials: true }
        );
        const tokensPriceResult = await tokensPriceResponse.json();

        setQuotes({
          usdcAmount: tokensPriceResult?.data?.usdcAmount,
          sechAmount: tokensPriceResult?.data?.sechAmount
            ? Number(tokensPriceResult?.data?.sechAmount)?.toFixed(2)
            : "0",
          gatewayFee: tokensPriceResult?.data?.gatewayFee,
        });
      } catch (error) {
        console.error("Error fetching tokens amounts:", error);
      } finally {
        setIsAmountsLoading(false);
      }
    };

    getInitialQuote();
  }, []);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full w-full"
        noValidate={true}
      >
        <div className="flex flex-1 overflow-y-auto pb-10">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-4 bg-[#2F2F32] py-4 px-3 rounded-[12px]">
              <FormField
                control={form.control}
                name="fiatAmount"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-3">
                    <FormLabel className="text-white text-base leading-[18px] font-semibold">
                      Spend Amount
                    </FormLabel>
                    <FormControl className="!my-0">
                      <div className="relative">
                        <Input
                          min={20}
                          type="number"
                          step="any"
                          placeholder={`Amount of USD to spend...`}
                          {...field}
                          onChange={onFiatAmountChange}
                          onInvalid={(e) => e.preventDefault()}
                          className="px-3 py-[18px] h-[48px] bg-black/10 border-white/10 text-[#ffffff] placeholder:text-[#888888] rounded-[8px] text-sm leading-[18px]"
                        />
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                          <span className="text-sm font-semibold text-[#ffffff] leading-[18px]">
                            USD
                          </span>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* <FormField
          control={form.control}
          name="paymentMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select Payment Method</FormLabel>
              <FormControl>
                <PaymentMethodSelect
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        /> */}

              <div className="flex flex-col gap-3">
                <h3 className="text-[#ffffff] font-medium text-base leading-[18px]">
                  How Buying SECH Works
                </h3>
                <ul className="text-[#BABABB] flex flex-col gap-[8px]">
                  <li className="flex flex-row items-center gap-2 text-[13px] leading-[15px]">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_30751_878)">
                        <path
                          d="M8.00016 1.33331V14.6666M12.7142 3.28593L3.28612 12.714M14.6668 7.99998H1.3335M12.7142 12.714L3.28612 3.28593"
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_30751_878">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <span>Pay with debit card via Banxa</span>
                  </li>

                  <li className="flex flex-row items-center gap-2 text-[13px] leading-[15px]">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_30751_878)">
                        <path
                          d="M8.00016 1.33331V14.6666M12.7142 3.28593L3.28612 12.714M14.6668 7.99998H1.3335M12.7142 12.714L3.28612 3.28593"
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_30751_878">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <span>USDC is sent to service wallet</span>
                  </li>

                  <li className="flex flex-row items-center gap-2 text-[13px] leading-[15px]">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_30751_878)">
                        <path
                          d="M8.00016 1.33331V14.6666M12.7142 3.28593L3.28612 12.714M14.6668 7.99998H1.3335M12.7142 12.714L3.28612 3.28593"
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_30751_878">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <span>SECH is sent to your wallet</span>
                  </li>

                  <li className="flex flex-row items-center gap-2 text-[13px] leading-[15px]">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0"
                    >
                      <g clipPath="url(#clip0_30751_878)">
                        <path
                          d="M8.00016 1.33331V14.6666M12.7142 3.28593L3.28612 12.714M14.6668 7.99998H1.3335M12.7142 12.714L3.28612 3.28593"
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>

                    <span>
                      Service wallet automatically converts USDC → SECH
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="rounded-lg bg-[#2F2F32] py-4 px-3 text-sm text-[#BABABB]">
              {!isAmountsLoading ? (
                <div className="flex flex-col gap-3">
                  <h3 className="font-semibold text-base leading-[18px] text-[#ffffff]">
                    Summary
                  </h3>
                  <div className="">
                    <div className="flex flex-col gap-2 text-[13px] leading-[15px]">
                      <div className="flex justify-between">
                        <span>Amount Entered</span>
                        <span>${fiatAmount} USD</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Gateway Fee (1.99%)</span>
                        <span>${quotes.gatewayFee || "0"} USD</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Converted to USDC</span>
                        <span>≈ {quotes.usdcAmount || "0"} USDC</span>
                      </div>
                    </div>
                    <hr className="my-3 border-t border-white/20" />
                    <div className="flex justify-between font-semibold text-[#ffffff] text-sm leading-[18px]">
                      <span>SECH You'll Receive</span>
                      <span>~ {quotes.sechAmount || "0"} SECH</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-row items-center justify-center font-medium h-[120px] text-[#BABABB]">
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                  Calculating amounts...
                </div>
              )}
            </div>
            <p className="text-xs text-[#BABABB] text-start my-2">
              <span className="font-medium">Note:</span> SECH Token purchases
              via Banxa require a debit card only. Credit cards
              (Visa/Mastercard) are not supported. All payments are processed by
              Banxa, not the SECH App.
            </p>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 bg-[#18181B]">
          <div className="max-w-5xl mx-auto px-5 py-3">
            <Button
              type="submit"
              className="w-full bg-[#EF4444] rounded-[24px] shadow-[0_4px_36px_0_rgba(220,38,38,0.6)]"
              disabled={
                isOrderProcessing || isAmountsLoading || !quotes.usdcAmount
              }
            >
              {isOrderProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing request...
                </>
              ) : (
                "Continue with Banxa"
              )}
            </Button>
          </div>
        </div>
      </form>
      <ConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmBuy}
        title="Confirm Purchase"
        description={`You are leaving the SECH app and will be redirected to Banxa, our third-party payment provider, to complete your SECH Token purchase. Banxa's terms of use and privacy policy will apply to your transaction. SECH does not provide money-transmission services and is not responsible for Banxa's site or services. You'll be returned to SECH once your purchase is complete.`}
        confirmText="Continue to Banxa"
        cancelText="Stay in SECH"
        variant="logout"
      />
    </Form>
  );
}
